import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: '',
  });

  const fetchReviews = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data || []);
    } catch (err) {
      setError('Impossible de charger les avis');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setStats(response.data);
    } catch (err) {
      // Stats are optional, don't block UI
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews, fetchStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success('Merci pour votre avis ! Il sera publié après validation.');
      setFormData({ name: '', email: '', rating: 5, comment: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de l\'avis');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, size = 20, interactive = false, onChange }) => {
    const [hovered, setHovered] = useState(0);
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange && onChange(star)}
            onMouseEnter={() => interactive && setHovered(star)}
            onMouseLeave={() => interactive && setHovered(0)}
            className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''}
          >
            <Star
              size={size}
              className={`${
                star <= (hovered || rating)
                  ? 'fill-[#FACC15] text-[#FACC15]'
                  : 'text-gray-600'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-black pt-24">
      {/* Hero Section */}
      <section className="py-16 border-b border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-[0.3em] mb-4 block">
              Témoignages
            </span>
            <h1 className="font-['Teko'] text-5xl md:text-7xl text-white uppercase leading-none mb-6">
              AVIS CLIENTS
            </h1>
            <p className="font-['Barlow'] text-gray-400 max-w-2xl mx-auto text-lg">
              Découvrez ce que nos clients pensent de nos prestations et formations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      {stats.count > 0 && (
        <section className="py-12 border-b border-white/10">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="text-center">
                <div className="font-['Teko'] text-6xl text-[#FACC15]">{stats.average}</div>
                <StarRating rating={Math.round(stats.average)} size={24} />
                <p className="font-['Barlow'] text-gray-400 mt-2">{stats.count} avis</p>
              </div>
              <div className="w-px h-24 bg-white/10 hidden md:block" />
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center gap-3">
                    <span className="font-['Barlow'] text-gray-400 w-4">{star}</span>
                    <Star size={16} className="fill-[#FACC15] text-[#FACC15]" />
                    <div className="w-32 h-2 bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[#FACC15]"
                        style={{ width: `${stats.count > 0 ? (stats.distribution[star] / stats.count) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="font-['Barlow'] text-gray-500 text-sm w-8">
                      {stats.distribution[star] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Reviews List */}
          <div className="lg:col-span-2">
            <h2 className="font-['Teko'] text-3xl text-white mb-8">TÉMOIGNAGES</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12 bg-[#09090b] border border-white/10">
                <MessageSquare size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="font-['Barlow'] text-gray-500">
                  Aucun avis pour le moment. Soyez le premier à donner votre avis !
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <motion.div
                    key={review.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-[#09090b] border border-white/10 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-['Teko'] text-xl text-white">{review.name}</h3>
                        <p className="font-['Barlow'] text-gray-500 text-sm">
                          {formatDate(review.created_at)}
                        </p>
                      </div>
                      <StarRating rating={review.rating} size={18} />
                    </div>
                    <p className="font-['Barlow'] text-gray-300 leading-relaxed">
                      {review.comment}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Review Form */}
          <div>
            <div className="sticky top-32">
              <h2 className="font-['Teko'] text-3xl text-white mb-8">DONNER VOTRE AVIS</h2>
              
              <form onSubmit={handleSubmit} className="bg-[#09090b] border border-white/10 p-6 space-y-6">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-3 block">
                    Votre note
                  </Label>
                  <StarRating
                    rating={formData.rating}
                    size={32}
                    interactive={true}
                    onChange={(rating) => setFormData({...formData, rating})}
                  />
                </div>
                
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Votre nom *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-black border-white/20 text-white"
                    placeholder="Prénom Nom"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Email (optionnel)
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-black border-white/20 text-white"
                    placeholder="votre@email.com"
                  />
                </div>
                
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Votre commentaire *
                  </Label>
                  <Textarea
                    value={formData.comment}
                    onChange={(e) => setFormData({...formData, comment: e.target.value})}
                    className="bg-black border-white/20 text-white"
                    placeholder="Partagez votre expérience..."
                    rows={5}
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50"
                >
                  {submitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Envoyer mon avis
                    </>
                  )}
                </Button>
                
                <p className="font-['Barlow'] text-gray-500 text-xs text-center">
                  Votre avis sera publié après validation par notre équipe.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
